import React, { useState } from 'react';
import { validateContactForm } from '../../utils/validation';

const ContactForm = ({ className = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    topic: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form (client-side) if helper exists
    let canSubmit = true;
    try {
      const validation = validateContactForm?.(formData);
      console.log('Validation result:', validation);
      if (validation && validation.success === false) {
        setErrors(validation.errors || {});
        canSubmit = false;
      }
    } catch (err) {
      console.warn('Client validation threw:', err);
    }
    if (!canSubmit) return;

    console.log('Validation passed, making API call...');
    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await fetch('/api/support/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const responseData = await response.json();
        console.log('Success response:', responseData);
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          topic: '',
          message: ''
        });
      } else {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (_) {
          // ignore JSON parse errors
        }
        console.log('Error response:', errorData);
        if (errorData?.errors && typeof errorData.errors === 'object') {
          setErrors(errorData.errors);
        }
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`contact-form-container ${className}`}>
      <form onSubmit={handleSubmit} className="contact-form" noValidate>
        <div className="form-group">
          <label htmlFor="name" className="form-label">
            Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`form-input ${errors.name ? 'error' : ''}`}
            aria-describedby={errors.name ? 'name-error' : undefined}
            disabled={isSubmitting}
          />
          {errors.name && (
            <div id="name-error" className="form-error" role="alert">
              {errors.name}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="email" className="form-label">
            Email <span className="required">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={`form-input ${errors.email ? 'error' : ''}`}
            aria-describedby={errors.email ? 'email-error' : undefined}
            disabled={isSubmitting}
          />
          {errors.email && (
            <div id="email-error" className="form-error" role="alert">
              {errors.email}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="topic" className="form-label">
            Topic <span className="required">*</span>
          </label>
          <select
            id="topic"
            name="topic"
            value={formData.topic}
            onChange={handleInputChange}
            className={`form-select ${errors.topic ? 'error' : ''}`}
            aria-describedby={errors.topic ? 'topic-error' : undefined}
            disabled={isSubmitting}
          >
            <option value="">Select a topic</option>
            <option value="Support">Support</option>
            <option value="Feedback">Feedback</option>
            <option value="Bug">Bug</option>
          </select>
          {errors.topic && (
            <div id="topic-error" className="form-error" role="alert">
              {errors.topic}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="message" className="form-label">
            Message <span className="required">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            rows="5"
            className={`form-textarea ${errors.message ? 'error' : ''}`}
            aria-describedby={errors.message ? 'message-error' : undefined}
            disabled={isSubmitting}
          />
          {errors.message && (
            <div id="message-error" className="form-error" role="alert">
              {errors.message}
            </div>
          )}
        </div>

        <button
          type="submit"
          className="form-submit"
          disabled={isSubmitting || !formData.name || !formData.email || !formData.topic || !formData.message}
          aria-describedby={submitStatus ? 'submit-status' : undefined}
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>

        {submitStatus === 'success' && (
          <div id="submit-status" className="form-success" role="alert">
            Thank you! Your message has been sent successfully. We'll get back to you within 24-48 hours.
          </div>
        )}

        {submitStatus === 'error' && (
          <div id="submit-status" className="form-error" role="alert">
            Sorry, there was an error sending your message. Please try again or email us directly at support@lown.app.
          </div>
        )}
      </form>
    </div>
  );
};

export default ContactForm;
